'use client';

import { Fragment, cloneElement, type ReactElement } from 'react';

import type { GetResponseType } from '@/api/types/api';
import type { ApiResponseDefinition } from '@/api/types/api-definition';
import Image from '@/components/Image';
import {
  buildSpmPositionCode,
  getSpmPositionCode,
  padSpmPositionIndex,
  useGlobalSpmTerminal,
} from '@/components/Layout/spmTerminal';
import { useCustomerService } from '@/hooks/useCustomerService';
import { useTranslations } from '@/i18n/use-translations';
import { IS_US } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { setSession } from '@/utils/tools';
import { useSpmDomAttr } from '@/reporting/spm';
import {
  FacebookOutlined,
  HelpEmailOutlined,
  HelpOnlineOutlined,
  HelpPhoneOutlined,
  InstagramOutlined,
  PinterestOutlined,
  TiktokOutlined,
  TwitterOutlined,
  YoutubeOutlined,
} from '@zeelool/icon';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { FOOTER_SOCIAL_POSITION_PC } from '../../socialSpmPosition';
import { shouldShowUSFooterSubscribe } from '../../subscribeVisibility';
import CountrySiteList2026 from './CountrySiteList';
import Subscribe2026 from './Subscribe';
import SubscribeCoupon2026 from './SubscribeCoupon';
import { getFooterLegalLinks, LEGAL_LINKS_TEMPLATE_ID } from './legalLinks';
import FooterLegalItem from './FooterLegalItem';
import { getFooterPaymentModules, PAYMENT_TEMPLATE_ID } from './paymentModules';
import { getFooterCopyrightModules, COPYRIGHT_TEMPLATE_ID } from './copyrightModules';

interface FooterNavLink {
  /** 运营后台可能未配置跳转链接（如 popup 类型项），此处允许为空。 */
  href?: string | null;
  title: string;
}

interface FooterNavContent {
  describe?: string;
  layout_title?: string;
  list?: Array<FooterNavLink | null>;
}

interface Footer2026Props {
  navList: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  SDSocialMediaClick: (btn: string) => void;
  SDBottomNavTabClick: (
    first_tab_name: string,
    second_tab_name?: string,
    first_tab_index?: number,
    second_tab_index?: number,
    spmCnt?: string,
  ) => void;
  isShowSubscribe: boolean;
  deviceType: EPlatform;
}

/**
 * 循环内挂载点 Data SPM DOM 打标：hook 不能在 map 中调用，封为小组件把 data-spm
 * 克隆到唯一真实子元素上（不新增 DOM 层级）；开关关闭或编码表外时原样渲染，零 DOM 差异。
 */
function SpmDomNode({ code, children }: { code: string | undefined; children: ReactElement }) {
  const domSpm = useSpmDomAttr(code);
  return domSpm ? cloneElement(children, domSpm) : children;
}

export default function Footer2026({
  navList,
  SDSocialMediaClick,
  SDBottomNavTabClick,
  isShowSubscribe,
  deviceType,
}: Footer2026Props) {
  const bottomSubscribeBoxTxt =
    (navList[0]?.content as FooterNavContent | undefined)?.describe || '';
  const pathname = usePathname();
  const t = useTranslations('layout.footer');
  const router = useRouter();
  const isShowCouponSubscribe = !IS_US && isShowSubscribe;
  const isShowSubscribeUS = shouldShowUSFooterSubscribe(pathname);
  // 2026-pc Footer 仅在 WebOnly 分支渲染；全局组件 B 位固定 global_pc。
  const spmPage = useGlobalSpmTerminal(deviceType);

  // footer_navigation.footer_link_XX 为跨分组的扁平序号，先按列累计偏移。
  let runningLinkCount = 0;
  const columnLinkOffsets = navList.map((item) => {
    const list = (item.content as FooterNavContent | undefined)?.list;
    const offset = runningLinkCount;
    runningLinkCount += Array.isArray(list) ? list.length : 0;
    return offset;
  });

  const resolveFooterLinkSpmCnt = (columnIndex: number, navItemIndex: number) =>
    // 底部导航按坑位序号规则出码（footer_link_XX = 跨列扁平序号），表快照仅作核验基线。
    buildSpmPositionCode(
      spmPage,
      'footer_navigation',
      `footer_link_${padSpmPositionIndex(columnLinkOffsets[columnIndex] + navItemIndex + 1)}`,
    );

  // 国家站入口按 global_pc 表名称语义固定映射（footer_country_sites.country_site_entry_001~005）。
  const COUNTRY_SITE_POSITION: Record<string, string> = {
    US: 'country_site_entry_001',
    UK: 'country_site_entry_002',
    DE: 'country_site_entry_003',
    FR: 'country_site_entry_004',
    CA: 'country_site_entry_005',
  };

  // Data SPM DOM 打标：与各点击上报的坑位同源同值（查表无该坑位时 undefined，自然不打标）。
  const contactUsDomSpm = useSpmDomAttr(
    getSpmPositionCode(spmPage, 'footer_help', 'open_contact_us'),
  );
  const trackOrderDomSpm = useSpmDomAttr(
    getSpmPositionCode(spmPage, 'footer_help', 'open_track_order'),
  );
  const resolveSocialSpmCnt = (btn: string) => {
    const spmPosition = FOOTER_SOCIAL_POSITION_PC[btn];
    return spmPosition ? getSpmPositionCode(spmPage, 'footer_social', spmPosition) : undefined;
  };
  const socialDomSpm = {
    fb: useSpmDomAttr(resolveSocialSpmCnt('fb')),
    youtube: useSpmDomAttr(resolveSocialSpmCnt('youtube')),
    ins: useSpmDomAttr(resolveSocialSpmCnt('ins')),
    twitter: useSpmDomAttr(resolveSocialSpmCnt('twitter')),
    pinterest: useSpmDomAttr(resolveSocialSpmCnt('pinterest')),
    tiktok: useSpmDomAttr(resolveSocialSpmCnt('tiktok')),
    fbGroup: useSpmDomAttr(resolveSocialSpmCnt('fbGroup')),
  };
  // 国家站列表逐项打标：与 onCountryClick 上报同一 COUNTRY_SITE_POSITION 查表。
  const resolveCountrySiteSpmCnt = (shortName: string) =>
    COUNTRY_SITE_POSITION[shortName]
      ? getSpmPositionCode(spmPage, 'footer_country_sites', COUNTRY_SITE_POSITION[shortName])
      : undefined;

  const { openCustomerService } = useCustomerService({
    inAppH5: false,
  });

  const legalLinks = getFooterLegalLinks(navList);
  const paymentModules = getFooterPaymentModules(navList, env.NEXT_PUBLIC_S3_URL);
  const copyrightModules = getFooterCopyrightModules(navList);

  return (
    <footer
      className={`bg-black text-white ${pathname.includes('/goods-detail/') ? 'pb-[80px]' : ''}`}
    >
      {isShowSubscribeUS ? <Subscribe2026 /> : null}
      {isShowCouponSubscribe ? (
        <SubscribeCoupon2026 bottomSubscribeBoxTxt={bottomSubscribeBoxTxt} />
      ) : null}

      <div className="flex w-full items-start justify-between border-y border-[#6c6c6c] px-[72px] py-[24px]">
        <div className="flex shrink-0 flex-col items-start gap-[32px]">
          <b className="font-bb text-[48px] leading-[58px] text-white">{t('needHelp')}</b>

          <dl className="flex w-full flex-col gap-[20px]">
            <div className="flex min-h-[32px] items-center gap-[8px]">
              <dt className="flex size-[32px] shrink-0 items-center justify-center">
                <HelpOnlineOutlined className="text-[20px] text-white" />
              </dt>
              <dd className="text-[14px] leading-[17px]">
                {env.NEXT_PUBLIC_FULL_SITE_NAME === 'ZEELOOL FR' ? (
                  <button
                    type="button"
                    className="cursor-pointer border-0 bg-transparent p-0 font-bb text-white"
                    aria-label={t('liveChat')}
                    onClick={openCustomerService}
                  >
                    {t('liveChat')}
                  </button>
                ) : (
                  <Link
                    prefetch={false}
                    className="font-bb text-white"
                    aria-label={t('contactUs')}
                    href="/contact-zeelool"
                    onClick={(event) => {
                      event.preventDefault();
                      SDBottomNavTabClick(
                        'Need Help?',
                        'Contact Us',
                        0,
                        0,
                        getSpmPositionCode(spmPage, 'footer_help', 'open_contact_us'),
                      );
                      router.push('/contact-zeelool');
                    }}
                    {...contactUsDomSpm}
                  >
                    {t('contactUs')}
                  </Link>
                )}
              </dd>
            </div>

            <div className="flex min-h-[32px] items-center gap-[8px]">
              <dt className="flex size-[32px] shrink-0 items-center justify-center">
                <HelpEmailOutlined className="text-[24px] text-white" />
              </dt>
              <dd className="flex flex-col gap-[4px] text-[14px] leading-[17px]">
                {env.NEXT_PUBLIC_FULL_SITE_NAME === 'ZEELOOL FR' ? (
                  <Link
                    prefetch={false}
                    className="font-bb text-white"
                    aria-label={t('contactUs')}
                    href="/contact-zeelool"
                    onClick={(event) => {
                      event.preventDefault();
                      SDBottomNavTabClick(
                        'Need Help?',
                        'Contact Us',
                        0,
                        0,
                        getSpmPositionCode(spmPage, 'footer_help', 'open_contact_us'),
                      );
                      router.push('/contact-zeelool');
                    }}
                    {...contactUsDomSpm}
                  >
                    {t('ContactUsViaForm')}
                  </Link>
                ) : (
                  <span className="font-bb">{t('businessCooperation')}</span>
                )}
                <Link
                  prefetch={false}
                  className="font-br text-white"
                  href={`mailto:${env.NEXT_PUBLIC_MARKET_EMAIL}`}
                >
                  {env.NEXT_PUBLIC_MARKET_EMAIL}
                </Link>
              </dd>
            </div>

            {env.NEXT_PUBLIC_FOOTER_SHOW_TEL === 'true' ? (
              <div className="flex min-h-[32px] items-center gap-[8px]">
                <dt className="flex size-[32px] shrink-0 items-center justify-center">
                  <HelpPhoneOutlined className="text-[20px] text-white" />
                </dt>
                <dd className="flex flex-col gap-[4px] font-br text-white">
                  <span className="text-[14px] leading-[17px]">{t('callInfo')}</span>
                  <span className="text-[12px] leading-[14px]">
                    {env.NEXT_PUBLIC_CUSTOMER_SERVICE_TIME}
                  </span>
                </dd>
              </div>
            ) : null}
          </dl>

          <Link
            prefetch={false}
            href="/track-order"
            aria-label={t('trackOrder')}
            className="flex h-[40px] w-[152px] items-center justify-center rounded-[20px] bg-primary-purple font-bb text-[14px] leading-[17px] text-white"
            onClick={(event) => {
              event.preventDefault();
              SDBottomNavTabClick(
                'Need Help?',
                'Track Order',
                0,
                0,
                getSpmPositionCode(spmPage, 'footer_help', 'open_track_order'),
              );
              router.push('/track-order');
            }}
            {...trackOrderDomSpm}
          >
            {t('trackOrder')}
          </Link>

          <div className="flex flex-col gap-[12px]">
            <p className="font-br text-[14px] leading-[17px]">{t('followUs')}</p>
            <div className="flex items-center gap-[24px]">
              {env.NEXT_PUBLIC_FB ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_FB}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('fb')}
                  {...socialDomSpm.fb}
                >
                  <FacebookOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_YTB ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_YTB}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('youtube')}
                  {...socialDomSpm.youtube}
                >
                  <YoutubeOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_INS ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_INS}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('ins')}
                  {...socialDomSpm.ins}
                >
                  <InstagramOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_X ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_X}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('twitter')}
                  {...socialDomSpm.twitter}
                >
                  <TwitterOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_PIN ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_PIN}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('pinterest')}
                  {...socialDomSpm.pinterest}
                >
                  <PinterestOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_TT ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_TT}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('tiktok')}
                  {...socialDomSpm.tiktok}
                >
                  <TiktokOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
            </div>
            {env.NEXT_PUBLIC_WEB_SHOW_FB_GROUP === 'true' ? (
              <Link
                prefetch={false}
                href={env.NEXT_PUBLIC_FB_GROUP}
                aria-label={t('social.joinGroup')}
                target="_blank"
                rel="noopener nofollow noreferrer"
                onClick={() => SDSocialMediaClick('fbGroup')}
                {...socialDomSpm.fbGroup}
              >
                <Image
                  src={env.NEXT_PUBLIC_FB_GROUP_IMG}
                  alt={t('social.joinGroup')}
                  width={204}
                  height={48}
                />
              </Link>
            ) : null}
          </div>

          <CountrySiteList2026
            onCountryClick={(shortName) =>
              SDBottomNavTabClick(
                `Region_${shortName}`,
                undefined,
                undefined,
                undefined,
                COUNTRY_SITE_POSITION[shortName]
                  ? getSpmPositionCode(
                      spmPage,
                      'footer_country_sites',
                      COUNTRY_SITE_POSITION[shortName],
                    )
                  : undefined,
              )
            }
            resolveSpmCnt={resolveCountrySiteSpmCnt}
            deviceType={deviceType}
          />
        </div>

        <nav
          className="flex min-w-0 flex-1 flex-wrap items-start justify-end gap-[40px]"
          aria-label="Footer"
        >
          {navList.map((item, index) => {
            // 协议、支付和版权模板在底部独立展示，保留其他导航的原始埋点索引。
            if (
              item.template_id === LEGAL_LINKS_TEMPLATE_ID ||
              item.template_id === PAYMENT_TEMPLATE_ID ||
              item.template_id === COPYRIGHT_TEMPLATE_ID
            )
              return null;
            const content = item.content as FooterNavContent;

            // 政策栏等模板也有 list，但不是 href 导航，不能交给 Next Link。
            if (
              (item.template_id != null && item.template_id !== 1) ||
              !Array.isArray(content?.list)
            ) {
              return null;
            }

            return (
              <div key={item.id} className="w-[220px] shrink-0">
                <p className="mb-[16px] font-bb text-[16px] leading-[19px] text-white">
                  {content.layout_title}
                </p>
                <div className="flex flex-col items-start gap-[12px]">
                  {content.list.map((navItem, navItemIndex) => {
                    // 运营可能配置没有 href 的导航项（如 popup 类型）；无有效链接时跳过渲染，
                    // 避免 next/link 透传 null 或空串在开发模式抛错并导致整页 500。
                    if (typeof navItem?.href !== 'string' || !navItem.href.trim()) return null;
                    const href = navItem.href.trim();

                    return (
                      <SpmDomNode
                        key={`${navItem.title}-${navItemIndex}`}
                        code={resolveFooterLinkSpmCnt(index, navItemIndex)}
                      >
                        <Link
                          prefetch={false}
                          href={href}
                          className="font-br text-[14px] leading-[17px] text-[#999999] hover:text-white"
                          onClick={(event) => {
                            event.preventDefault();
                            SDBottomNavTabClick(
                              content.layout_title || '',
                              navItem.title,
                              index,
                              navItemIndex,
                              resolveFooterLinkSpmCnt(index, navItemIndex),
                            );
                            if (href.includes('goods-list')) {
                              setSession('zl_goodsListEnterPageScene', '全局导航');
                            }
                            if (href === env.NEXT_PUBLIC_COOKIE_TARGET) {
                              window.OneTrust.ToggleInfoDisplay();
                            } else if (
                              (href.includes('zeelool.com') || href.includes('xmslol.cn')) &&
                              !href.includes(env.NEXT_PUBLIC_HELP_CENTER_PATH)
                            ) {
                              const targetUrl = new URL(href);
                              const url = targetUrl.href.replace(targetUrl.origin, '');
                              router.push(url);
                            } else {
                              window.location.href = href;
                            }
                          }}
                        >
                          {navItem.title}
                        </Link>
                      </SpmDomNode>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      <div className="flex w-full flex-col items-center pt-[24px]">
        {legalLinks.length > 0 ? (
          <ul className="flex w-full flex-wrap items-center justify-center gap-x-[32px] gap-y-[16px] px-[160px] pb-[12px] pt-[24px] font-br text-[14px] uppercase leading-[17px] text-white">
            {legalLinks.map((legalLink, index) => (
              <Fragment key={legalLink.key}>
                {index > 0 ? <li aria-hidden="true">|</li> : null}
                <li className="whitespace-nowrap text-center">
                  <FooterLegalItem item={legalLink} className="text-white" />
                </li>
              </Fragment>
            ))}
          </ul>
        ) : null}

        {paymentModules.map((payment) => (
          <div
            key={payment.key}
            className="flex w-full items-center justify-center px-[160px] py-[12px]"
          >
            <div className="flex min-w-0 flex-wrap items-center justify-center gap-[12px]">
              {payment.title ? (
                <p className="whitespace-nowrap font-bb text-[14px] leading-[17px] text-white">
                  {payment.title}
                </p>
              ) : null}
              <div
                className="flex flex-wrap items-center justify-center gap-[8px]"
                aria-hidden="true"
              >
                {payment.images.map((src, index) => (
                  <Image
                    key={`${src}:${index}`}
                    src={src}
                    alt=""
                    width={40}
                    height={24}
                    unoptimized
                    className="h-[24px] w-[40px] shrink-0 rounded-[2px] object-contain"
                  />
                ))}
              </div>
            </div>
          </div>
        ))}

        {copyrightModules.map((copyright) => (
          <p
            key={copyright.key}
            className="w-full px-[160px] pb-[40px] pt-[12px] text-center font-br text-[14px] leading-[17px] text-white"
          >
            {copyright.text}
          </p>
        ))}
      </div>
    </footer>
  );
}
